// frontend/src/features/inventory/pages/MaterialDetailPage.tsx
// ADR-074: نمایش یک MaterialFamily با تمام revision هایش
// Rule INV-FE-02: PUBLISHED و SUPERSEDED فقط read-only
// Rule INV-FE-04: revision_no و status در فرم نمایش داده نمی‌شوند

import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import {
  useV1InventoryMaterialsRetrieve,
  useV1InventoryMaterialsDeactivateCreate,
  useV1InventoryMaterialsActivateCreate,
  useV1InventoryMaterialRevisionsList,
  useV1InventoryMaterialRevisionsPublishCreate,
} from "@/api/generated/v1/v1";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { PageBanner } from "@/shared/components/PageBanner";

const STATUS_LABEL: Record<string, { label: string; color: string; textColor: string }> = {
  draft: { label: "پیش‌نویس", color: "rgba(255,200,0,0.15)", textColor: "#d4a800" },
  published: { label: "منتشرشده", color: "rgba(0,200,80,0.12)", textColor: "#00b050" },
  superseded: { label: "جایگزین‌شده", color: "rgba(128,128,128,0.15)", textColor: "#888" },
};

const MATERIAL_TYPE_LABEL: Record<string, string> = {
  raw_material: "مواد اولیه",
  semi_finished: "نیمه‌ساخته",
  finished_product: "محصول نهایی",
  spare_part: "قطعه یدکی",
};

function InfoItem({ label, value, dir = "rtl" }: { label: string; value?: string | null; dir?: string }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
      <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: "0.88rem", fontWeight: 600 }} dir={dir}>{value}</span>
    </div>
  );
}

export function MaterialDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const canManage = usePermission("inventory.manage");

  const {
    data: family,
    isLoading: familyLoading,
    isError: familyError,
    refetch: refetchFamily,
  } = useV1InventoryMaterialsRetrieve(id!, {
    query: { enabled: !!id },
  });

  const { mutate: deactivate, isPending: isDeactivating } = useV1InventoryMaterialsDeactivateCreate({
    mutation: {
      onSuccess: () => refetchFamily(),
      onError: (err: any) => alert(err?.response?.data?.detail ?? "خطا در غیرفعال‌سازی"),
    }
  });

  const { mutate: activate, isPending: isActivating } = useV1InventoryMaterialsActivateCreate({
    mutation: {
      onSuccess: () => refetchFamily(),
      onError: (err: any) => alert(err?.response?.data?.detail ?? "خطا در فعال‌سازی"),
    }
  });

  const {
    data: revisionsData,
    isLoading: revisionsLoading,
    refetch: refetchRevisions,
  } = useV1InventoryMaterialRevisionsList({ family: id }, {
    query: { enabled: !!id },
  });

  // client-side filter
  const revisions = (revisionsData?.results ?? []).filter(
    (r) => r.family === id
  );

  const { mutate: publish, isPending: isPublishing } =
    useV1InventoryMaterialRevisionsPublishCreate({
      mutation: {
        onSuccess: () => refetchRevisions(),
        onError: (err: any) => {
          alert(err?.response?.data?.detail ?? "خطا در publish کردن");
        },
      },
    });

  if (!id) {
    navigate("/inventory/materials");
    return null;
  }

  if (familyLoading) {
    return (
      <div className="p-8 flex-center">
        <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
      </div>
    );
  }

  if (familyError || !family) {
    return (
      <div className="p-8">
        <p style={{ color: "red" }}>متریال یافت نشد.</p>
        <button
          className="btn btn-secondary mt-4"
          onClick={() => navigate("/inventory/materials")}
        >
          بازگشت به لیست
        </button>
      </div>
    );
  }

  const latestPublished = revisions.find((r) => r.status === "published");

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }} dir="rtl">
      <PageBanner
        title={`جزئیات خانواده قطعه: ${family.code}`}
        subtitle={latestPublished ? `نسخه فعال: ${latestPublished.name}` : "بدون نسخه فعال"}
        icon="📦"
        helpLink="/help/inventory/material-detail"
      >
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory/materials")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
          {canManage && (
            <>
              <button
                disabled={isDeactivating || isActivating}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "12px",
                  background: family.is_active ? "rgba(255, 0, 0, 0.15)" : "rgba(0, 255, 0, 0.15)",
                  color: family.is_active ? "#ffcccc" : "#ccffcc",
                  border: `1px solid ${family.is_active ? "rgba(255, 0, 0, 0.3)" : "rgba(0, 255, 0, 0.3)"}`,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
                onClick={() =>
                  family.is_active
                    ? deactivate({ id: family.id, data: family as any })
                    : activate({ id: family.id, data: family as any })
                }
              >
                {isDeactivating || isActivating ? "..." : family.is_active ? "غیرفعال‌سازی" : "فعال‌سازی"}
              </button>
              <button
                onClick={() => navigate(`/inventory/materials/${id}/revisions/create`)}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>➕</span>
                <span>ریویژن جدید</span>
              </button>
            </>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "1100px", margin: "0 auto", padding: "1.5rem", width: "100%" }}>
        {/* ===== Material Info ===== */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <span style={{
              padding: "0.2rem 0.75rem", borderRadius: "100px", fontSize: "0.8rem",
              background: family.is_active ? "rgba(0,200,80,0.1)" : "rgba(255,0,0,0.1)",
              color: family.is_active ? "#00b050" : "#cc0000",
              border: `1px solid ${family.is_active ? "rgba(0,200,80,0.3)" : "rgba(255,0,0,0.3)"}`,
            }}>
              {family.is_active ? "فعال" : "غیرفعال"}
            </span>
            <span style={{
              padding: "0.2rem 0.75rem", borderRadius: "100px", fontSize: "0.8rem",
              background: "rgba(100,150,255,0.1)", color: "var(--color-primary)",
            }}>
              {MATERIAL_TYPE_LABEL[family.material_type] ?? family.material_type}
            </span>
          </div>
          <div style={{ display: "flex", gap: "1.25rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.82rem" }}>
              <span style={{ color: "var(--color-text-muted)" }}>واحد انبار (پایه): </span>
              <strong dir="ltr">{family.uom_code || "—"}</strong>
            </span>
            {latestPublished && (latestPublished as any).production_uom_code && (
              <span style={{ fontSize: "0.82rem" }}>
                <span style={{ color: "var(--color-text-muted)" }}>واحد تولید (BOM): </span>
                <strong dir="ltr">{(latestPublished as any).production_uom_code}</strong>
              </span>
            )}
          </div>
        </div>

      {/* ===== Custom Fields (Family level) ===== */}
      <div className="mb-6">
        <CustomFieldsPanel
          targetContentType="inventory.materialfamily"
          targetObjectId={id}
          context={{ material_type: family.material_type }}
          editable={canManage}
        />
      </div>

      {/* ===== Revisions ===== */}
      <div style={{ borderTop: "1px solid var(--glass-border)", paddingTop: "1.5rem" }}>
        <div className="flex-between mb-4" style={{ borderBottom: "1px solid var(--glass-border)", paddingBottom: "0.75rem" }}>
          <h2 className="font-bold" style={{ fontSize: "1rem" }}>ریویژن‌ها</h2>
          <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
            {revisions.length} ریویژن
          </span>
        </div>

        {revisionsLoading ? (
          <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری ریویژن‌ها...</p>
        ) : revisions.length === 0 ? (
          <div className="p-6" style={{ textAlign: "center", color: "var(--color-text-muted)", background: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
            هیچ ریویژنی ثبت نشده.
            {canManage && (
              <div className="mt-3">
                <button className="btn btn-primary" onClick={() => navigate(`/inventory/materials/${id}/revisions/create`)}>
                  + ایجاد ریویژن اول
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {revisions.map((rev) => {
              const statusInfo = STATUS_LABEL[rev.status] ?? { label: rev.status, color: "rgba(128,128,128,0.15)", textColor: "#888" };
              const isDraft = rev.status === "draft";
              const r = rev as any;

              return (
                <div
                  key={rev.id}
                  style={{
                    padding: "1.25rem 1.5rem",
                    background: isDraft ? "rgba(255,200,0,0.03)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isDraft ? "rgba(255,200,0,0.25)" : "var(--glass-border)"}`,
                    borderRadius: "10px",
                  }}
                >
                  {/* Rev Header */}
                  <div className="flex-between mb-3">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <span className="font-bold" style={{ fontSize: "1rem" }}>{rev.name}</span>
                      <span style={{
                        padding: "0.18rem 0.6rem", borderRadius: "100px",
                        background: statusInfo.color, color: statusInfo.textColor,
                        fontSize: "0.78rem", fontWeight: 600,
                      }}>
                        {statusInfo.label}
                      </span>
                      <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }} dir="ltr">
                        Rev {rev.revision_no}
                      </span>
                    </div>

                    {canManage && isDraft && (
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}
                          disabled={isPublishing}
                          onClick={() => navigate(`/inventory/materials/${id}/revisions/${rev.id}/edit`)}
                        >
                          ویرایش
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}
                          disabled={isPublishing}
                          onClick={() => publish({ id: rev.id, data: {} as any })}
                        >
                          {isPublishing ? "..." : "Publish ✓"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Rev Info Grid — واحدها و مشخصات پایه */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                    gap: "1rem 2rem",
                    padding: "1rem 1.25rem",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "8px",
                    border: "1px solid var(--glass-border)",
                    marginBottom: "0.75rem",
                  }}>
                    <InfoItem label="کد فنی (SKU)" value={rev.sku} dir="ltr" />
                    <InfoItem
                      label="واحد انبار (پایه)"
                      value={r.stock_uom_code || family.uom_code}
                      dir="ltr"
                    />
                    <InfoItem
                      label="واحد تولید (BOM)"
                      value={r.production_uom_code || (r.stock_uom_code !== r.production_uom_code ? null : r.stock_uom_code)}
                      dir="ltr"
                    />
                    {rev.published_at && (
                      <InfoItem
                        label="تاریخ انتشار"
                        value={new Date(rev.published_at).toLocaleDateString("fa-IR")}
                      />
                    )}
                  </div>

                  {rev.description && (
                    <p style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", marginBottom: "0.75rem" }}>
                      {rev.description}
                    </p>
                  )}

                  {/* Custom fields per revision (فیلدهای سفارشی مانند گروه قطعه، دسته وسیله نقلیه، تولیدکننده...) */}
                  <CustomFieldsPanel
                    targetContentType="inventory.materialrevision"
                    targetObjectId={rev.id}
                    context={{ material_type: family.material_type }}
                    editable={canManage && isDraft}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      </div>
    </div>
  );
}